<template>
  <div class="dropdown ts-field-select w-100" @keydown.esc="open = false">
    <button
      :id="id"
      class="btn form-select ts-field-select-toggle w-100 text-start"
      type="button"
      :aria-expanded="String(open)"
      :aria-controls="`${id}-menu`"
      @click="open = !open"
    >
      <span data-ts-field-select-label>{{ selected ? selected.name : placeholder }}</span>
    </button>
    <ul
      :id="`${id}-menu`"
      ref="menu"
      class="dropdown-menu ts-field-select-menu w-100 shadow"
      :class="{ show: open }"
      :style="menuStyle"
      :aria-labelledby="id"
    >
      <li v-for="item in items" :key="item.id">
        <button
          class="dropdown-item search-option"
          :class="{ active: item.id === value }"
          type="button"
          :aria-pressed="String(item.id === value)"
          @click="select(item.id)"
        >
          <span class="search-option-label">{{ item.name }}</span>
          <span class="search-option-count">{{ formatCount(item.count) }} {{ unit }}</span>
          <span class="search-option-check" aria-hidden="true"><i class="bi bi-check2"></i></span>
        </button>
      </li>
    </ul>
  </div>
</template>

<script lang="ts">
import {
  Component, Prop, Vue, Watch,
} from 'vue-property-decorator';
import { formatCount } from '@/helpers/formatCount';

export interface FieldOption {
  id: number
  name: string
  count: number
}

// Single choice of an occupational field (cluster) with its count, v-model = field id
@Component
export default class TsFieldSelect extends Vue {
  @Prop({ required: true }) readonly id!: string;

  @Prop({ default: null }) readonly value!: number | null;

  @Prop({ default: () => [] }) readonly items!: FieldOption[];

  @Prop({ default: '' }) readonly placeholder!: string;

  @Prop({ default: '' }) readonly unit!: string;

  open = false;

  // Placement like Bootstrap's dynamic dropdown: below, or above when there is more room there
  menuStyle: Record<string, string> = {};

  get selected() {
    return this.items.find((item) => item.id === this.value) || null;
  }

  formatCount = formatCount;

  // Clicking the selected field again clears the choice
  select(id: number) {
    this.$emit('input', id === this.value ? null : id);
    this.open = false;
  }

  @Watch('open')
  onOpen(open: boolean) {
    if (open) {
      this.place();
      document.addEventListener('click', this.closeOutside);
    } else {
      document.removeEventListener('click', this.closeOutside);
    }
  }

  place() {
    const gap = 16;
    const designMax = 360;
    const toggle = this.$el.getBoundingClientRect();
    const below = window.innerHeight - toggle.bottom - gap;
    const above = toggle.top - gap;
    const openUp = below < Math.min(designMax, 240) && above > below;
    const maxHeight = `${Math.max(160, Math.min(designMax, openUp ? above : below))}px`;
    this.menuStyle = openUp
      ? {
        top: 'auto', bottom: '100%', left: '0', marginBottom: '4px', maxHeight,
      }
      : {
        top: '100%', bottom: 'auto', left: '0', marginTop: '4px', maxHeight,
      };
  }

  beforeDestroy() {
    document.removeEventListener('click', this.closeOutside);
  }

  closeOutside(event: MouseEvent) {
    if (!this.$el.contains(event.target as Node)) this.open = false;
  }
}
</script>
